import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { getAssetUrl } from "../utils/assets";

const DEFAULTS = [
  {
    name: "Student",
    role: "ITI Trainee",
    text: "Great practical training and supportive faculty. The workshops made me job-ready.",
    avatar: "https://api.dicebear.com/8.x/initials/svg?seed=ST&backgroundColor=fce7f3",
  },
  {
    name: "Parent",
    role: "Guardian",
    text: "Clear guidance and a disciplined environment. We saw real improvement in confidence.",
    avatar: "https://api.dicebear.com/8.x/initials/svg?seed=PA&backgroundColor=ffedd5",
  },
  {
    name: "Alumni",
    role: "Placed Candidate",
    text: "The trade training and placement help were excellent. Highly recommended for skills.",
    avatar: "https://api.dicebear.com/8.x/initials/svg?seed=AL&backgroundColor=fef9c3",
  },
];

const Testimonials = ({ items = DEFAULTS, intervalMs = 4200 }) => {
  const list = useMemo(() => (Array.isArray(items) && items.length ? items : DEFAULTS), [items]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % list.length), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs, list.length]);

  const active = list[index] || list[0];
  const imgSrc = active?.image ? getAssetUrl(active.image) : active?.avatar || "";

  return (
    <section id="student-success-stories" className="section container">
      <h2 className="section-title-gradient text-center">Student Success Stories</h2>
      <p className="section-lead text-center font-semibold tracking-wide leading-relaxed text-slate-600">
        Real stories from trainees and parents — practical training that leads to confidence and careers.
      </p>

      <div className="mx-auto mt-8 max-w-3xl">
        <div className="relative overflow-hidden rounded-2xl border border-slate-200/70 bg-white/80 p-0 shadow-md shadow-pink-200/20 transition-shadow duration-300 hover:shadow-lg">
          <div
            className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-pink-400 to-yellow-300"
            aria-hidden
          />

          <AnimatePresence mode="wait">
            <motion.div
              key={`${active.name}-${index}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
              className="grid grid-cols-1 gap-0 sm:grid-cols-[220px_1fr]"
            >
              <div className="relative h-[220px] w-full overflow-hidden bg-slate-100 sm:h-full">
                {imgSrc ? (
                  <img
                    src={imgSrc}
                    alt={active.name ? `${active.name} testimonial` : "Testimonial"}
                    className="h-full w-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                ) : null}
                <div
                  className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/0 to-white/30"
                  aria-hidden
                />
              </div>
              <div className="flex flex-col gap-4 p-6">
                {active.text ? (
                  <p className="text-base font-semibold tracking-wide leading-relaxed text-slate-700">
                    “{active.text}”
                  </p>
                ) : null}
                <div className="min-w-0">
                  {active.name ? <p className="truncate text-sm font-semibold text-slate-900">{active.name}</p> : null}
                  {active.role ? <p className="truncate text-xs text-slate-500">{active.role}</p> : null}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="mt-6 flex items-center justify-center gap-2">
            {list.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to story ${i + 1}`}
                onClick={() => setIndex(i)}
                className={`h-2.5 w-2.5 rounded-full transition ${
                  i === index ? "bg-pink-400 shadow-[0_0_18px_rgba(236,72,153,0.35)]" : "bg-slate-200 hover:bg-slate-300"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;

